/**
 * PortalFormService — business logic orchestration for portal form intake.
 *
 * Follows FERP service pattern: sits between repositories and hooks.
 * Orchestrates multi-repository operations (portal lookup → workflow fetch → submit).
 * Owns validation logic and form data transformation.
 */
import type { Portal, ExposedWorkflow, FormSubmission, FormField } from '../types';
import { PortalRepository, portalRepository } from '../repositories/PortalRepository';
import { WorkflowRepository, workflowRepository } from '../repositories/WorkflowRepository';
import { SubmissionRepository, submissionRepository, type SubmitFormInput } from '../repositories/SubmissionRepository';

export interface PortalFormContext {
  portal: Portal;
  workflow: ExposedWorkflow;
}

export interface ValidationError {
  fieldId: string;
  message: string;
}

export class PortalFormService {
  constructor(
    private readonly portalRepo: PortalRepository,
    private readonly workflowRepo: WorkflowRepository,
    private readonly submissionRepo: SubmissionRepository,
  ) {}

  async loadFormContext(portalSlug: string, workflowSlug: string): Promise<PortalFormContext> {
    const portal = await this.portalRepo.getBySlug(portalSlug);
    if (!portal) {
      throw new Error(`Portal not found: ${portalSlug}`);
    }

    const workflow = await this.workflowRepo.getBySlug(portal.id, workflowSlug);
    if (!workflow) {
      throw new Error(`Workflow not found: ${workflowSlug}`);
    }

    return { portal, workflow };
  }

  validateFormData(workflow: ExposedWorkflow, data: Record<string, unknown>): ValidationError[] {
    const errors: ValidationError[] = [];
    const allFields = workflow.steps.flatMap((step) => step.fields);

    for (const field of allFields) {
      const value = data[field.id];
      const fieldErrors = this.validateField(field, value);
      errors.push(...fieldErrors);
    }

    return errors;
  }

  async submitForm(
    portalSlug: string,
    workflowSlug: string,
    data: Record<string, unknown>,
  ): Promise<FormSubmission> {
    const input: SubmitFormInput = { portalSlug, workflowSlug, data };
    return this.submissionRepo.submit(input);
  }

  private validateField(field: FormField, value: unknown): ValidationError[] {
    const errors: ValidationError[] = [];

    if (field.required && (value === undefined || value === null || value === '')) {
      errors.push({ fieldId: field.id, message: `${field.label} is required` });
      return errors;
    }

    if (value === undefined || value === null || value === '') {
      return errors;
    }

    const strValue = String(value);

    if (field.minLength && strValue.length < field.minLength) {
      errors.push({ fieldId: field.id, message: `${field.label} must be at least ${field.minLength} characters` });
    }

    if (field.maxLength && strValue.length > field.maxLength) {
      errors.push({ fieldId: field.id, message: `${field.label} must be at most ${field.maxLength} characters` });
    }

    if (field.pattern) {
      const regex = new RegExp(field.pattern);
      if (!regex.test(strValue)) {
        errors.push({ fieldId: field.id, message: `${field.label} format is invalid` });
      }
    }

    return errors;
  }
}

export const portalFormService = new PortalFormService(
  portalRepository,
  workflowRepository,
  submissionRepository,
);
